import { useState, useCallback, useEffect, useRef, memo } from "react";
import { ActivityIndicator } from "react-native";
import { View } from "@src/components";
import { useVideoPlayer } from "expo-video";
import { useSharedValue, useAnimatedStyle, withTiming, useAnimatedReaction, Easing } from "react-native-reanimated";
import type { Clip } from "../types/clip";
import { VideoProgressBar } from "./VideoProgressBar";
import { NoClipPlaceholder } from "./NoClipPlaceholder";
import { ClipInfoHeader } from "./ClipInfoHeader";
import { VideoThumbnailOverlay } from "./VideoThumbnailOverlay";
import { VideoControlsOverlay } from "./VideoControlsOverlay";
import { VideoPlayerView, type VideoViewRef } from "./VideoPlayerView";
import { ExitFullscreenButton } from "./ExitFullscreenButton";
import { AnimatedFullscreenButton } from "./AnimatedFullscreenButton";
import { AnimatedMuteButton } from "./AnimatedMuteButton";
import { VideoLoadingOverlay } from "./VideoLoadingOverlay";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { track } from "@visioni/a3s-analytics";
import { ClipMuted, ClipUnmuted } from "@visioni/a3s-media/app";
import { cameraScope } from "@src/features/cameras/analytics";
import { useTenantId } from "@src/features/tenants";

interface ClipVideoPlayerProps {
  clip: Clip | null;
  /** The site and camera the clip is from, sent with the mute events. */
  siteId: string;
  cameraKey: string;
  isLoading?: boolean;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  screenHeight: number;
  screenWidth: number;
  clipIndex?: number;
  totalClips?: number;
  orientation?: "portrait" | "landscape";
  selectedHour: number;
  /**
   * Autoplay muted on load. Defaults to true (the clips page). Set false to
   * show the first frame paused — used by dumping search, where a rapidly
   * scanned clip should hold still until the reviewer chooses to play.
   */
  autoPlay?: boolean;
  onViewed?: (clip: Clip) => void;
  /**
   * Opt-in override for the clip's max height cap (px). When omitted the player
   * caps itself at 40% of the window height (the clips-page default). Dumping
   * Search passes the measured height of its available area so the clip grows
   * to fill it (still 16:9, centered) instead of being pinned to that 40%.
   */
  maxHeightOverride?: number;
  /**
   * Opt-in fixed height (px) for the video box. When set, the box becomes a
   * `width:100% × boxHeight` region and the video is `contain`-fit inside it —
   * the WHOLE frame always shows, with black bars filling any aspect-ratio
   * mismatch (the box no longer enforces 16:9). Use this when the source clips
   * aren't 16:9 (e.g. 4:3 cameras): the legacy 16:9 box cropped the bottom of a
   * taller frame because `overflow:hidden` clipped what didn't fit. When omitted
   * the box keeps its legacy 16:9 aspect-ratio-derived sizing (unchanged for the
   * clips page).
   */
  boxHeight?: number;
  /**
   * Opt-in "fill the parent" mode. When true, the video box becomes
   * `width:100% × height:100%` and the video is `contain`-fit inside it — so
   * the parent owns the sizing/shape and the player just fills it. Use this when
   * the clip sits inside a container that already shapes and bounds the media
   * (e.g. the MediaCarousel stage, which is a centred, aspect-shaped box): a
   * fixed `boxHeight` would mismatch the parent at widths where the parent is
   * shorter than that height, and `overflow:hidden` would then crop. Takes
   * precedence over `boxHeight`. Ignored in fullscreen/landscape.
   */
  fillParent?: boolean;
  /**
   * Opt-in external pause. When true, the player is paused (and kept paused).
   * Defaults to false (the player plays/pauses under its own controls). Used by
   * multi-slot containers like the MediaCarousel, which keep inactive slides
   * *mounted* (hidden, for instant paging + preserved buffers) rather than
   * unmounting them: without this, a clip the reviewer started keeps playing —
   * and, if unmuted, keeps audible — off-screen after they page away. The
   * carousel passes `paused={!isActive}` so only the visible slide can play.
   */
  paused?: boolean;
}

function formatHourRange(hour: number): string {
  const formatHour = (h: number): string => {
    if (h === 0 || h === 24) {
      return "12:00am";
    }
    if (h < 12) {
      return `${h}:00am`;
    }
    if (h === 12) {
      return "12:00pm";
    }
    return `${h - 12}:00pm`;
  };

  const startHour = formatHour(hour);
  const endHour = formatHour(hour + 1);
  return `${startHour} - ${endHour}`;
}

export const ClipVideoPlayer = memo(
  ({
    clip,
    siteId,
    cameraKey,
    isLoading = false,
    onPrevious,
    onNext,
    hasPrevious = false,
    hasNext = false,
    screenHeight,
    screenWidth,
    clipIndex,
    totalClips,
    orientation = "portrait",
    selectedHour,
    autoPlay = true,
    onViewed,
    maxHeightOverride,
    boxHeight,
    fillParent = false,
    paused = false,
  }: ClipVideoPlayerProps) => {
    const { theme } = useUnistyles();
    const tenantId = useTenantId();
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [controlsVisible, setControlsVisible] = useState(false);
    const videoViewRef = useRef<VideoViewRef>(null);
    const hideControlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const viewedClipIdRef = useRef<string | null>(null);

    const isLandscape = orientation === "landscape";

    // Controls fade animation
    const controlsOpacity = useSharedValue(0);

    // Sync controlsVisible state with opacity animation
    useAnimatedReaction(
      () => controlsVisible || isFullscreen,
      (shouldShow) => {
        controlsOpacity.value = withTiming(shouldShow ? 1 : 0, {
          duration: 150,
          easing: Easing.out(Easing.ease),
        });
      },
      [controlsVisible, isFullscreen],
    );

    const controlsAnimatedStyle = useAnimatedStyle(() => ({
      opacity: controlsOpacity.value,
      // Gate interactivity on the *actual* animated opacity, not the React
      // `controlsVisible` state. On web the fade opacity (reanimated) and a
      // `pointerEvents` prop (React state) can desync, leaving a faded-out
      // control that still captures clicks. Tying pointer-events to opacity
      // guarantees a fully transparent control is never clickable. We use
      // "auto"/"none" (valid CSS) rather than "box-none" because reanimated
      // writes this via the style path on web, where "box-none" is not valid
      // CSS. NOTE: this style is applied to the *button-sized* controls (the
      // centred button row and the corner mute/fullscreen buttons) — NOT to the
      // full-cover overlay layer. The overlay layer uses a static
      // `pointerEvents="box-none"` JSX prop so empty-area taps fall through to
      // the video's tap-to-dismiss while its button children stay hit-testable
      // on native (see VideoControlsOverlay).
      pointerEvents: controlsOpacity.value > 0 ? "auto" : "none",
    }));

    const player = useVideoPlayer(clip?.videoUrl || null, (player) => {
      player.loop = false;
      // Clips always start muted; the user can opt in to audio per-clip via the mute toggle.
      player.muted = true;
      if (autoPlay) player.play();
    });

    const [isPlaying, setIsPlaying] = useState(player.playing);
    const [isEnded, setIsEnded] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);

    // Reset states when clip changes
    useEffect(() => {
      setVideoLoaded(false);
      setIsPlaying(false);
      setIsEnded(false);
      // Re-mute on clip change. The useVideoPlayer setup callback already re-mutes the
      // underlying player, but mutedChange won't fire if the previous clip was also muted,
      // so we need to reset the local state explicitly to keep the icon in sync.
      setIsMuted(true);
      setControlsVisible(false);
      setCurrentTime(0);
      setDuration(0);
      viewedClipIdRef.current = null;
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, [clip?.id]);

    // Play video when clip changes
    useEffect(() => {
      if (!autoPlay) return;
      if (clip?.videoUrl && player) {
        // Play after a short delay to ensure player source is updated and ready
        const timeoutId = setTimeout(() => {
          /* console.log("ClipVideoPlayer: attempting play", {
          id: clip?.id,
          url: clip?.videoUrl,
          isPlaying: player.playing,
        }); */
          if (!player.playing) {
            try {
              player.play();
            } catch (error: unknown) {
              console.warn("Failed to play video:", error);
            }
          }
        }, 300);
        return () => clearTimeout(timeoutId);
      }
    }, [clip?.id, clip?.videoUrl, player, autoPlay]);

    // External pause (e.g. a MediaCarousel slide going inactive). Pause the
    // underlying player whenever `paused` is true so a started clip doesn't keep
    // playing/decoding — or, if unmuted, keep making noise — off-screen after
    // the reviewer pages away. Also hide the controls so a hidden slide doesn't
    // leave a stale visible-controls state to flash on when it becomes active
    // again. We don't auto-resume when `paused` flips back to false: the paused
    // first frame is the report's intended resting state (autoPlay={false}), and
    // the reviewer re-initiates playback on the now-active slide.
    useEffect(() => {
      if (paused) {
        player.pause();
        setControlsVisible(false);
      }
    }, [paused, player]);

    useEffect(() => {
      const playingSubscription = player.addListener("playingChange", ({ isPlaying }) => {
        setIsPlaying(isPlaying);
      });
      const endSubscription = player.addListener("playToEnd", () => {
        setIsEnded(true);
        setControlsVisible(true);
      });
      const mutedSubscription = player.addListener("mutedChange", ({ muted }) => {
        setIsMuted(muted);
      });
      return () => {
        playingSubscription.remove();
        endSubscription.remove();
        mutedSubscription.remove();
      };
    }, [player]);

    // Track playback progress
    useEffect(() => {
      // Update duration when it becomes available
      if (player.duration > 0) {
        setDuration(player.duration);
      }

      // Poll for current time while playing
      let intervalId: ReturnType<typeof setInterval> | null = null;

      const updateProgress = () => {
        if (player.currentTime >= 0) {
          setCurrentTime(player.currentTime);
        }
        if (player.duration > 0 && duration !== player.duration) {
          setDuration(player.duration);
        }
      };

      // Update immediately
      updateProgress();

      // Poll every 100ms for smooth progress updates
      intervalId = setInterval(updateProgress, 100);

      return () => {
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, [player, duration]);

    const handlePlayPause = useCallback(() => {
      if (isEnded) {
        // Restart from beginning
        player.currentTime = 0;
        setIsEnded(false);
        player.play();
      } else if (isPlaying) {
        player.pause();
      } else {
        player.play();
      }
    }, [isPlaying, isEnded, player]);

    const handleMuteToggle = useCallback(() => {
      const next = !isMuted;
      player.muted = next;
      if (clip) {
        track(next ? ClipMuted : ClipUnmuted, { ...cameraScope(tenantId, siteId, cameraKey), clip_id: clip.id });
      }
      // Keep the controls visible long enough for the user to see the icon flip — mirrors
      // the auto-hide behaviour in handleVideoTap. Skipped in fullscreen, which has its
      // own native chrome and doesn't auto-hide.
      if (!isFullscreen) {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
        hideControlsTimeoutRef.current = setTimeout(() => {
          setControlsVisible(false);
          hideControlsTimeoutRef.current = null;
        }, 3000);
      }
    }, [isMuted, player, clip, tenantId, siteId, cameraKey, isFullscreen]);

    const handleFullscreen = useCallback(async () => {
      if (videoViewRef.current) {
        if (isFullscreen) {
          // Exit fullscreen - let VideoView handle it naturally
          try {
            if (typeof videoViewRef.current.exitFullscreen === "function") {
              await videoViewRef.current.exitFullscreen();
            } else {
              await videoViewRef.current.enterFullscreen();
            }
          } catch (error) {
            console.warn("Failed to exit fullscreen:", error);
          }
        } else {
          // Enter fullscreen
          await videoViewRef.current.enterFullscreen();
        }
      }
    }, [isFullscreen]);

    const handleFullscreenEnter = useCallback(() => {
      setIsFullscreen(true);
      setControlsVisible(true);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    const handleFullscreenExit = useCallback(() => {
      setIsFullscreen(false);
      setControlsVisible(false);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }
    }, []);

    // Toggle controls on tap
    const handleVideoTap = useCallback(() => {
      // Clear existing timeout
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
        hideControlsTimeoutRef.current = null;
      }

      // Toggle visibility
      const newVisible = !controlsVisible;
      // console.log("ClipVideoPlayer: Video tapped, toggling controls", { isFullscreen, controlsVisible, newVisible });
      setControlsVisible(newVisible);

      // In fullscreen, don't auto-hide. Otherwise, auto-hide after 3 seconds if showing
      if (newVisible && !isFullscreen) {
        hideControlsTimeoutRef.current = setTimeout(() => {
          // console.log("ClipVideoPlayer: Hiding controls after timeout");
          setControlsVisible(false);
          hideControlsTimeoutRef.current = null;
        }, 3000);
      }
    }, [isFullscreen, controlsVisible]);

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
      };
    }, []);

    const handleFirstFrameRender = useCallback(() => {
      setVideoLoaded(true);
      if (clip && viewedClipIdRef.current !== clip.id) {
        viewedClipIdRef.current = clip.id;
        onViewed?.(clip);
      }
    }, [clip, onViewed]);

    if (!clip) {
      if (isLoading) {
        return (
          <>
            <View mb="3xl" mt="lg" />
            <View
              style={[
                styles.loadingContainer,
                boxHeight !== undefined ?
                  // Fixed-height box mode — match the main container so the
                  // loading state occupies the same space as the resolved clip.
                  { height: boxHeight }
                : {
                    width: screenWidth - theme.spacing.lg * 2,
                    aspectRatio: 16 / 9,
                    maxHeight: maxHeightOverride ?? screenHeight * 0.4,
                    // Match videoContainer: cap width so the 16:9 box never
                    // exceeds the height cap (which would otherwise clip it).
                    maxWidth: (maxHeightOverride ?? screenHeight * 0.4) * (16 / 9),
                  },
              ]}
              alignItems="center"
              justifyContent="center"
            >
              <ActivityIndicator size="large" color={theme.colors.text as string} />
            </View>
          </>
        );
      }
      return (
        <NoClipPlaceholder
          screenWidth={screenWidth}
          screenHeight={screenHeight}
          hourRangeLabel={formatHourRange(selectedHour)}
        />
      );
    }

    return (
      <View style={[styles.container, fillParent && styles.containerFill]}>
        {/* Clip info - below video */}
        {!isLandscape && clipIndex !== undefined && totalClips !== undefined && (
          <ClipInfoHeader
            hourRangeLabel={formatHourRange(selectedHour)}
            clipIndex={clipIndex}
            totalClips={totalClips}
          />
        )}
        <View
          style={[
            styles.videoContainer,
            // Box-sizing branches. `styles.videoContainer` no longer carries an
            // aspectRatio (it can't be reliably cleared by a later `undefined`
            // in a merged StyleSheet), so each branch sets its own sizing:
            //   • fullscreen/landscape: keep the 16:9 baseline but drop the
            //     maxHeight/maxWidth caps (native chrome drives the actual
            //     fullscreen size). This MUST still give the in-page box a
            //     height: the child Pressable is absolute inset:0, so with no
            //     aspectRatio the box would collapse to 0 — invisible in
            //     landscape and a flash/jump on fullscreen exit. On main this
            //     height came from an aspectRatio on the base videoContainer;
            //     now that it's per-branch, this branch must re-assert it.
            //   • fillParent: fill the parent (width+height 100%) → the parent
            //     owns the shape/bounds and the video contain-fits inside it.
            //   • boxHeight mode: explicit height, NO aspectRatio → the video
            //     contain-fits inside width×boxHeight (black bars for any aspect
            //     mismatch, never cropped).
            //   • default: legacy 16:9 box capped by height (and the matching
            //     width so overflow:hidden can't crop the aspect-derived height).
            isFullscreen || isLandscape ? { aspectRatio: 16 / 9 }
            : fillParent ? styles.videoContainerFill
            : boxHeight !== undefined ? { height: boxHeight }
            : {
                aspectRatio: 16 / 9,
                maxHeight: maxHeightOverride ?? screenHeight * 0.4,
                maxWidth: (maxHeightOverride ?? screenHeight * 0.4) * (16 / 9),
              },
          ]}
        >
          {!videoLoaded && (
            <>
              {clip.thumbnailUrl && <VideoThumbnailOverlay thumbnailUrl={clip.thumbnailUrl} />}
              <VideoLoadingOverlay />
            </>
          )}

          <VideoPlayerView
            ref={videoViewRef}
            player={player}
            isFullscreen={isFullscreen}
            isLandscape={isLandscape}
            onTap={handleVideoTap}
            onFullscreenEnter={handleFullscreenEnter}
            onFullscreenExit={handleFullscreenExit}
            onFirstFrameRender={handleFirstFrameRender}
          />

          {/* Video controls overlay with fade animation */}
          <VideoControlsOverlay
            isPlaying={isPlaying}
            isEnded={isEnded}
            hasPrevious={hasPrevious}
            hasNext={hasNext}
            animatedStyle={controlsAnimatedStyle}
            onPrevious={onPrevious}
            onNext={onNext}
            onPlayPause={handlePlayPause}
          />

          {/* Fullscreen/Exit fullscreen button - bottom right with fade animation */}
          <AnimatedFullscreenButton
            isFullscreen={isFullscreen}
            animatedStyle={controlsAnimatedStyle}
            onPress={handleFullscreen}
          />

          {/* Mute/Unmute button - bottom left with fade animation (mirrors fullscreen) */}
          <AnimatedMuteButton isMuted={isMuted} animatedStyle={controlsAnimatedStyle} onPress={handleMuteToggle} />

          {/* Exit fullscreen button overlay - always visible when in fullscreen */}
          {isFullscreen && <ExitFullscreenButton onPress={handleFullscreen} />}

          {/* Progress bar at the bottom */}
          {!isFullscreen && !isLandscape && <VideoProgressBar currentTime={currentTime} duration={duration} />}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
  },
  // Fill mode: the outer wrapper stretches to the parent so the video box
  // below can take 100% height. Paired with `fillParent`.
  containerFill: {
    flex: 1,
    minHeight: 0,
  },
  videoContainer: {
    width: "100%",
    position: "relative",
    alignSelf: "center",
    overflow: "hidden",
  },
  // Fill mode: the video box fills the parent entirely; the parent owns the
  // shape and bounds, the video `contain`-fits inside.
  videoContainerFill: {
    height: "100%",
    flex: 1,
  },
  loadingContainer: {
    alignSelf: "center",
    borderRadius: theme.spacing.md,
    backgroundColor: "#64748B14",
  },
}));
